import { devPalettePresets, type DevPalettePreset } from './presets';
import {
  fontPresets,
  spacingOptions,
  radiusOptions,
  DEV_FONT_HREF,
  DEFAULT_FONT_ID,
  DEFAULT_SPACING_ID,
  DEFAULT_RADIUS_ID,
} from './axes';

const STORAGE_KEY = 'lophoc_dev_theme';
// Legacy key from the color-only version — migrated on first read.
const LEGACY_PALETTE_KEY = 'lophoc_dev_palette';

const COLOR_TOKEN_KEYS = Object.keys(devPalettePresets[0]?.tokens ?? {});
const FONT_STYLE_ID = 'dev-font-display';
const FONT_LINK_ID = 'dev-font-link';

export interface DevThemeState {
  palette: string | null;
  font: string;
  spacing: string;
  radius: string;
}

export const DEFAULT_THEME: DevThemeState = {
  palette: null,
  font: DEFAULT_FONT_ID,
  spacing: DEFAULT_SPACING_ID,
  radius: DEFAULT_RADIUS_ID,
};

/* ------------------------------- persistence ------------------------------ */

export function readDevTheme(): DevThemeState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULT_THEME, ...(JSON.parse(raw) as Partial<DevThemeState>) };
  } catch {
    /* ignore malformed */
  }
  // Migrate the old color-only key if present.
  const legacy = localStorage.getItem(LEGACY_PALETTE_KEY);
  if (legacy) return { ...DEFAULT_THEME, palette: legacy };
  return { ...DEFAULT_THEME };
}

export function storeDevTheme(state: DevThemeState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  localStorage.removeItem(LEGACY_PALETTE_KEY);
}

/* --------------------------------- color ---------------------------------- */

function applyColor(preset: DevPalettePreset | null) {
  const root = document.documentElement;
  if (!preset) {
    for (const key of COLOR_TOKEN_KEYS) root.style.removeProperty(key);
    return;
  }
  for (const [key, value] of Object.entries(preset.tokens)) {
    root.style.setProperty(key, value);
  }
}

/* ---------------------------------- font ---------------------------------- */

function ensureFontLink() {
  if (document.getElementById(FONT_LINK_ID)) return;
  const link = document.createElement('link');
  link.id = FONT_LINK_ID;
  link.rel = 'stylesheet';
  link.href = DEV_FONT_HREF;
  document.head.appendChild(link);
}

function ensureHeadingRule() {
  if (document.getElementById(FONT_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = FONT_STYLE_ID;
  // Headings + explicit display opt-ins pick up the paired display face.
  style.textContent =
    'h1,h2,h3,h4,h5,h6,.font-display{font-family:var(--font-display,var(--font-sans));}';
  document.head.appendChild(style);
}

function applyFont(id: string) {
  const root = document.documentElement;
  const preset = fontPresets.find((f) => f.id === id) ?? fontPresets[0]!;
  if (preset.id === DEFAULT_FONT_ID) {
    root.style.removeProperty('--font-sans');
    root.style.removeProperty('--font-display');
    return;
  }
  ensureFontLink();
  ensureHeadingRule();
  root.style.setProperty('--font-sans', preset.sans);
  root.style.setProperty('--font-display', preset.display);
}

/* ------------------------------- scale axes ------------------------------- */

function applySpacing(id: string) {
  const root = document.documentElement;
  const opt = spacingOptions.find((s) => s.id === id);
  if (!opt || id === DEFAULT_SPACING_ID) root.style.removeProperty('--spacing');
  else root.style.setProperty('--spacing', opt.value);
}

function applyRadius(id: string) {
  const root = document.documentElement;
  const opt = radiusOptions.find((r) => r.id === id);
  if (!opt || id === DEFAULT_RADIUS_ID) root.style.removeProperty('--radius');
  else root.style.setProperty('--radius', opt.value);
}

/* --------------------------------- public --------------------------------- */

export function applyDevTheme(state: DevThemeState) {
  const preset = devPalettePresets.find((p) => p.id === state.palette) ?? null;
  applyColor(preset);
  applyFont(state.font);
  applySpacing(state.spacing);
  applyRadius(state.radius);
}

export function resetDevTheme() {
  applyDevTheme(DEFAULT_THEME);
  storeDevTheme(DEFAULT_THEME);
}

/** Call once at app init (dev only) to re-apply the stored theme on reload. */
export function initDevPalette() {
  if (!import.meta.env.DEV) return;
  applyDevTheme(readDevTheme());
}
